import React from 'react'
import { AlertCircle, Check, RotateCcw, Save, Search, X } from 'lucide-react'
import { Button, Input, Toggle } from '@/components/ui'
import { cn } from '@/lib/cn'
import { SECTION_GROUPS, type SectionId, type SectionMeta } from '../constants/sections'

/* -------------------------------------------------------------------------- */
/*  Field layout                                                              */
/* -------------------------------------------------------------------------- */

export interface FieldGridProps {
  children: React.ReactNode
  /** Column ceiling. Numeric fields read better in threes; text fields in twos. */
  columns?: 2 | 3
  className?: string
}

/**
 * Responsive field row. Fields keep a comfortable minimum measure and wrap
 * rather than compressing, so a narrow viewport never produces a 90px input.
 */
export function FieldGrid({
  children,
  columns = 3,
  className,
}: FieldGridProps): React.ReactElement {
  return (
    <div
      className={cn(
        'grid gap-x-5 gap-y-4 sm:grid-cols-2',
        columns === 3 && 'xl:grid-cols-3',
        className
      )}
    >
      {children}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/*  Pane and field groups                                                     */
/* -------------------------------------------------------------------------- */

export interface SettingsPaneProps {
  title: string
  description: string
  /** Right-aligned pane-level control, e.g. a status select. */
  action?: React.ReactNode
  children: React.ReactNode
  className?: string
}

/**
 * One settings pane: a heading block and its fields. The pane is the page's
 * only card, so field groups inside it use rules rather than nested borders.
 */
export function SettingsPane({
  title,
  description,
  action,
  children,
  className,
}: SettingsPaneProps): React.ReactElement {
  return (
    <section className={cn('min-w-0', className)} aria-labelledby="org-pane-title">
      <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
        <div className="min-w-0 max-w-2xl">
          <h2
            id="org-pane-title"
            className="text-base font-semibold tracking-[-0.01em] text-gray-900 dark:text-gray-100"
          >
            {title}
          </h2>
          <p className="mt-1 text-sm leading-6 text-gray-600 dark:text-gray-400">{description}</p>
        </div>
        {action ? <div className="shrink-0">{action}</div> : null}
      </div>
      <div className="mt-6 space-y-8">{children}</div>
    </section>
  )
}

export interface FieldGroupProps {
  label: string
  /** Optional clarifier shown beside the group label. */
  hint?: string
  children: React.ReactNode
  className?: string
}

/**
 * A labelled cluster of related fields inside a pane. Groups are separated by
 * space and a hairline rule — never by another card.
 */
export function FieldGroup({
  label,
  hint,
  children,
  className,
}: FieldGroupProps): React.ReactElement {
  return (
    <div
      className={cn(
        'border-t border-gray-200/70 pt-6 first:border-t-0 first:pt-0 dark:border-gray-700/70',
        className
      )}
    >
      <div className="mb-4 flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h3 className="text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">
          {label}
        </h3>
        {hint ? (
          <p className="text-xs text-gray-500 dark:text-gray-500">{hint}</p>
        ) : null}
      </div>
      {children}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/*  Controls                                                                  */
/* -------------------------------------------------------------------------- */

export interface LabeledToggleRowProps {
  label: string
  description?: string
  checked: boolean
  onChange: (checked: boolean) => void
  disabled?: boolean
  className?: string
}

/**
 * A switch with its label and consequence. Rows stack into a single bordered
 * list via {@link ToggleList} rather than each carrying its own box.
 */
export function LabeledToggleRow({
  label,
  description,
  checked,
  onChange,
  disabled = false,
  className,
}: LabeledToggleRowProps): React.ReactElement {
  return (
    <div
      className={cn(
        'px-4 py-3.5 transition-colors',
        !disabled && 'hover:bg-white/70 dark:hover:bg-gray-800/40',
        className
      )}
    >
      <Toggle
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        label={label}
        description={description}
        align="end"
      />
    </div>
  )
}

export interface ToggleListProps {
  children: React.ReactNode
  className?: string
}

/** Groups {@link LabeledToggleRow}s into one divided list. */
export function ToggleList({ children, className }: ToggleListProps): React.ReactElement {
  return (
    <div
      className={cn(
        'divide-y divide-gray-200/70 overflow-hidden rounded-xl border border-gray-200 bg-gray-50/50 dark:divide-gray-700/70 dark:border-gray-700 dark:bg-gray-900/25',
        className
      )}
    >
      {children}
    </div>
  )
}

export interface NumberFieldProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label: string
  error?: string
  /** Trailing unit rendered inside the control, e.g. "min". */
  unit?: string
  hint?: string
}

export const NumberField = React.forwardRef<HTMLInputElement, NumberFieldProps>(
  ({ label, error, unit, hint, className, id: idProp, name, ...rest }, ref) => {
    const id = idProp ?? name
    const describedBy =
      [error && id ? `${id}-error` : null, hint && id ? `${id}-hint` : null]
        .filter(Boolean)
        .join(' ') || undefined

    return (
      <div className="w-full min-w-0">
        <label
          htmlFor={id}
          className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400"
        >
          {label}
        </label>
        <div className="relative">
          <input
            ref={ref}
            id={id}
            name={name}
            type="number"
            inputMode="decimal"
            aria-invalid={!!error}
            aria-describedby={describedBy}
            className={cn(
              'box-border h-11 w-full rounded-xl border border-gray-200 bg-white/95 px-3.5 text-sm tabular-nums text-gray-900 shadow-[0_1px_2px_rgba(15,23,42,0.05)] transition-[border-color,box-shadow] duration-150 placeholder:text-gray-400 focus:border-primary/40 focus:outline-none focus:ring-4 focus:ring-primary/10 dark:border-gray-700 dark:bg-gray-800/90 dark:text-gray-100 dark:placeholder:text-gray-500',
              unit && 'pr-14',
              error && 'border-danger focus:border-danger/70 focus:ring-danger/10',
              className
            )}
            {...rest}
          />
          {unit && (
            <span className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-xs font-medium text-gray-400 dark:text-gray-500">
              {unit}
            </span>
          )}
        </div>
        {hint && !error && (
          <p id={id ? `${id}-hint` : undefined} className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
            {hint}
          </p>
        )}
        {error && (
          <p id={id ? `${id}-error` : undefined} className="mt-1.5 text-xs text-danger">
            {error}
          </p>
        )}
      </div>
    )
  }
)

NumberField.displayName = 'NumberField'

/* -------------------------------------------------------------------------- */
/*  Section rail                                                              */
/* -------------------------------------------------------------------------- */

export interface SectionRailProps {
  sections: readonly SectionMeta[]
  activeId: SectionId
  onSelect: (id: SectionId) => void
  /** Per-section dirty / error markers driven by form state. */
  status: Record<SectionId, { dirty: boolean; error: boolean }>
  query: string
  onQueryChange: (value: string) => void
}

/**
 * Persistent left rail listing every settings pane. Replaces the previous
 * tabs-plus-sub-nav pair so no pane is more than one click away.
 */
export function SectionRail({
  sections,
  activeId,
  onSelect,
  status,
  query,
  onQueryChange,
}: SectionRailProps): React.ReactElement {
  const groups = SECTION_GROUPS.filter((group) =>
    sections.some((section) => section.group === group.id)
  )

  return (
    <div className="flex min-h-0 flex-col gap-3">
      <div className="relative">
        <Search
          className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
          aria-hidden
        />
        <Input
          value={query}
          onChange={(event) => onQueryChange(event.target.value)}
          placeholder="Filter settings"
          aria-label="Filter settings"
          className={cn('h-10 pl-9', query && 'pr-9')}
        />
        {query && (
          <button
            type="button"
            onClick={() => onQueryChange('')}
            aria-label="Clear filter"
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 dark:hover:bg-gray-700 dark:hover:text-gray-200"
          >
            <X className="h-3.5 w-3.5" aria-hidden />
          </button>
        )}
      </div>

      <nav
        className="min-h-0 flex-1 overflow-y-auto pb-1"
        aria-label="Organization settings sections"
      >
        {sections.length === 0 ? (
          <p className="px-2 py-3 text-sm text-gray-500 dark:text-gray-400">
            No settings match “{query}”.
          </p>
        ) : (
          <div className="space-y-5">
            {groups.map((group) => (
              <div key={group.id}>
                <p className="px-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-gray-400 dark:text-gray-500">
                  {group.label}
                </p>
                <ul className="mt-1.5 space-y-0.5">
                  {sections
                    .filter((section) => section.group === group.id)
                    .map((section) => {
                      const isActive = section.id === activeId
                      const state = status[section.id]
                      const Icon = section.icon

                      return (
                        <li key={section.id}>
                          <button
                            type="button"
                            onClick={() => onSelect(section.id)}
                            aria-current={isActive ? 'true' : undefined}
                            className={cn(
                              'group flex w-full items-center gap-2.5 rounded-lg px-2 py-2 text-left text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
                              isActive
                                ? 'bg-primary/10 font-medium text-primary'
                                : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-700/50 dark:hover:text-gray-100'
                            )}
                          >
                            <Icon
                              className={cn(
                                'h-4 w-4 shrink-0 transition-colors',
                                isActive
                                  ? 'text-primary'
                                  : 'text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300'
                              )}
                              strokeWidth={isActive ? 2 : 1.75}
                              aria-hidden
                            />
                            <span className="min-w-0 flex-1 truncate">{section.label}</span>
                            {state?.error ? (
                              <span
                                className="h-1.5 w-1.5 shrink-0 rounded-full bg-danger"
                                title={`${section.label} has validation errors`}
                              />
                            ) : state?.dirty ? (
                              <span
                                className="h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500"
                                title={`${section.label} has unsaved changes`}
                              />
                            ) : null}
                          </button>
                        </li>
                      )
                    })}
                </ul>
              </div>
            ))}
          </div>
        )}
      </nav>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/*  Save bar                                                                  */
/* -------------------------------------------------------------------------- */

export interface SaveBarProps {
  dirtyCount: number
  canUpdate: boolean
  isPending: boolean
  hasValidationErrors: boolean
  apiError: string | null
  validationMessage: string | null
  onDiscard: () => void
}

/**
 * Save bar docked to the bottom of the settings card. It reports what is
 * pending in words, so the disabled Save button is never the only explanation.
 */
export function SaveBar({
  dirtyCount,
  canUpdate,
  isPending,
  hasValidationErrors,
  apiError,
  validationMessage,
  onDiscard,
}: SaveBarProps): React.ReactElement | null {
  if (!canUpdate) return null

  const hasDirty = dirtyCount > 0
  const message = apiError ?? validationMessage

  return (
    <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3 border-t border-gray-200 bg-gray-50/80 px-5 py-3 dark:border-gray-700 dark:bg-gray-900/40">
      <div className="min-w-0">
        <p className="flex items-center gap-2 text-sm font-medium text-gray-900 dark:text-gray-100">
          {message ? (
            <AlertCircle className="h-4 w-4 shrink-0 text-danger" aria-hidden />
          ) : hasDirty ? (
            <span
              className="h-2 w-2 shrink-0 rounded-full bg-amber-500"
              aria-hidden
            />
          ) : (
            <Check className="h-4 w-4 shrink-0 text-green-600 dark:text-green-400" aria-hidden />
          )}
          {message ? (
            <span className="text-danger" role="alert">
              {message}
            </span>
          ) : hasDirty ? (
            <span>
              {dirtyCount} unsaved {dirtyCount === 1 ? 'change' : 'changes'}
            </span>
          ) : (
            <span>All changes saved</span>
          )}
        </p>
      </div>

      <div className="flex shrink-0 items-center gap-2">
        <Button
          type="button"
          variant="secondary"
          size="md"
          disabled={!hasDirty || isPending}
          onClick={onDiscard}
          className="gap-1.5"
        >
          <RotateCcw className="h-3.5 w-3.5" strokeWidth={2} aria-hidden />
          Discard
        </Button>
        <Button
          type="submit"
          size="md"
          disabled={!hasDirty || hasValidationErrors} loading={isPending}
          className="gap-1.5 min-w-[7rem]"
        >
          <Save className="h-3.5 w-3.5" strokeWidth={2} aria-hidden />
          {isPending ? 'Saving…' : 'Save changes'}
        </Button>
      </div>
    </div>
  )
}
